import { ColumnsType } from 'antd/es/table';
import React, { FC, memo, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { ServerTableActions, ServerTableRowSelectionActions } from 'shared/ui/ServerTable/types';
import { useGetCurrencySymbol } from 'app/appState';
import { getBoxRentalPriceAmount, Box, useGetUnitsQuery } from 'entities/Box';
import { ServerTable } from 'shared/ui/ServerTable';
import { stepperModalActions } from 'features/StepperModal';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { useTableFilterContext } from 'features/TableFilter';
import { BoxesListFilter } from './BoxesListFilter';
import { transformBoxesListFilters } from './utils/transformBoxesListFilters';
import { createContractModalActions } from '../../../model/slice/createContractModalSlice';
import { getSelectedWarehouse } from '../../../model/selectors/getSelectedWarehouse';
import { getSizeCodeTypeString } from 'entities/SizeCode';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { formatPrice } from 'shared/utils/helpers/formatPrice';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';

export const SelectBoxStep: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'booking', 'common']);

  const dispatch = useAppDispatch();

  const selectedWarehouse = useAppSelector(getSelectedWarehouse);

  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();

  const currencySymbol = useGetCurrencySymbol();

  const tableActions: Array<ServerTableActions<Box>> = useMemo(
    () => [
      {
        name: t('Filters', { ns: 'boxes' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ],
    [t, setFiltersOpened],
  );

  const columns = useMemo<ColumnsType<Box>>(
    () => [
      {
        title: t('Unit'),
        key: 'name',
        render: (_, record) => record.name,
      },
      {
        title: <span className="flex justify-center">{t('Size')}</span>,
        key: 'sizeCode.square',
        sorter: true,

        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => {
          const size = `${formatDecimal(roundNumber(record?.height))} м (${t('H', { ns: 'booking' })}) × ${formatDecimal(
            roundNumber(record?.width),
          )} м (${t('W', {
            ns: 'booking',
          })}) × ${formatDecimal(roundNumber(record?.length))} м (${t('D', { ns: 'booking' })})`;

          return (
            <div className="flex flex-col">
              <span className="text-center">
                {formatDecimal(record.sizeCode.square)} {getSizeCodeTypeString(record.sizeCode.sizeCodeType)}
              </span>
              <span className="text-xs text-center">{size}</span>
            </div>
          );
        },
      },
      {
        title: t('Location'),
        key: 'floor.floorId',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => `${getLocalizedString(record.floor?.name) || ''}`,
      },
      {
        title: t('Price'),
        key: 'price',
        render: (_, record) => {
          const price = getBoxRentalPriceAmount(record);

          return price ? `${formatPrice(price)} ${currencySymbol}` : '';
        },
      },
    ],
    [currencySymbol, t],
  );

  const rowSelectionActions: Array<ServerTableRowSelectionActions<Box>> = [
    {
      name: t('Next'),
      onClick: (selectedElements) => {
        dispatch(createContractModalActions.setSelectedBox(selectedElements[0])); // only one box can be selected
        dispatch(stepperModalActions.changeActiveStepIndex(3));
      },
    },
  ];

  return (
    <>
      <ServerTable
        columns={columns}
        rowKey="boxId"
        search
        title={t('Choose unit')}
        selectionType="radio"
        rowSelectionActions={rowSelectionActions}
        fetchData={useGetUnitsQuery}
        onAllFiltersClear={clearAllFilters}
        defaultFilters={{
          warehouseId: selectedWarehouse?.warehouseId,
          isAvailable: true,
          ...transformBoxesListFilters(appliedFilters),
        }}
        tags={tags}
        tableActions={tableActions}
      />
      <BoxesListFilter />
    </>
  );
});
